import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Search, Filter, Eye, Truck, CheckCircle, XCircle, Clock, Package } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [page, status]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/orders?page=${page}&limit=20`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const viewOrderDetail = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedOrder(data);
      setShowDetail(true);
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (s) => {
    const config = {
      pending: { bg: "bg-yellow-100 text-yellow-800", icon: Clock, label: "Bekliyor" },
      confirmed: { bg: "bg-blue-100 text-blue-800", icon: CheckCircle, label: "Onaylandı" },
      processing: { bg: "bg-purple-100 text-purple-800", icon: Package, label: "Hazırlanıyor" },
      shipped: { bg: "bg-indigo-100 text-indigo-800", icon: Truck, label: "Kargoda" },
      delivered: { bg: "bg-green-100 text-green-800", icon: CheckCircle, label: "Teslim Edildi" },
      cancelled: { bg: "bg-red-100 text-red-800", icon: XCircle, label: "İptal" }
    };
    const c = config[s] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg}`}>
        <c.icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  const getPaymentBadge = (s) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
      refunded: "bg-gray-100 text-gray-800"
    };
    const labels = {
      pending: "Bekliyor",
      paid: "Ödendi",
      failed: "Başarısız",
      refunded: "İade"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[s]}`}>
        {labels[s]}
      </span>
    );
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Sipariş Yönetimi</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Toplam Sipariş</p>
          <p className="text-2xl font-bold">{stats.total || 0}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg shadow">
          <p className="text-sm text-yellow-600">Bekleyen</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.pending || 0}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg shadow">
          <p className="text-sm text-blue-600">Onaylanan</p>
          <p className="text-2xl font-bold text-blue-700">{stats.confirmed || 0}</p>
        </div>
        <div className="bg-indigo-50 p-4 rounded-lg shadow">
          <p className="text-sm text-indigo-600">Kargoda</p>
          <p className="text-2xl font-bold text-indigo-700">{stats.shipped || 0}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow">
          <p className="text-sm text-green-600">Teslim Edilen</p>
          <p className="text-2xl font-bold text-green-700">{stats.delivered || 0}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg shadow">
          <p className="text-sm text-red-600">İptal</p>
          <p className="text-2xl font-bold text-red-700">{stats.cancelled || 0}</p>
        </div>
      </div>

      {/* Revenue Card */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-orange-100">Toplam Satış Tutarı</p>
            <p className="text-4xl font-bold">{(stats.total_revenue || 0).toLocaleString()} ₺</p>
          </div>
          <div className="text-right">
            <p className="text-orange-100">Toplam Komisyon</p>
            <p className="text-3xl font-bold">{(stats.total_commission || 0).toLocaleString()} ₺</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[300px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Sipariş no, müşteri adı veya telefon..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              Ara
            </button>
          </form>
          
          <div className="flex gap-2">
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Tüm Durumlar</option>
              <option value="pending">Bekleyen</option>
              <option value="confirmed">Onaylanan</option>
              <option value="processing">Hazırlanan</option>
              <option value="shipped">Kargoda</option>
              <option value="delivered">Teslim Edilen</option>
              <option value="cancelled">İptal</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sipariş No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Müşteri</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ürün Sayısı</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tutar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ödeme</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                  </div>
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                  Sipariş bulunamadı
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm font-medium text-orange-600">
                      #{order.order_number || order.id?.slice(-8).toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium">{order.customer_name || order.shipping_address?.name || "-"}</p>
                      <p className="text-sm text-gray-500">{order.customer_phone || order.shipping_address?.phone || "-"}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">{order.items?.length || 0}</td>
                  <td className="px-6 py-4 font-bold">{(order.total || 0).toLocaleString()} ₺</td>
                  <td className="px-6 py-4">{getPaymentBadge(order.payment_status)}</td>
                  <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString("tr-TR") : "-"}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => viewOrderDetail(order.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Detay"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
          >
            Önceki
          </button>
          <span className="px-4 py-2">
            Sayfa {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
          >
            Sonraki
          </button>
        </div>
      )}

      {/* Order Detail Modal */}
      {showDetail && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">
                Sipariş #{selectedOrder.order_number || selectedOrder.id?.slice(-8).toUpperCase()}
              </h2>
              <button
                onClick={() => setShowDetail(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Sipariş Tarihi</p>
                  <p className="font-medium">
                    {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString("tr-TR") : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Durum</p>
                  <p>{getStatusBadge(selectedOrder.status)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ödeme Durumu</p>
                  <p>{getPaymentBadge(selectedOrder.payment_status)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ödeme Yöntemi</p>
                  <p className="font-medium">{selectedOrder.payment_method || "-"}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">Müşteri Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Ad Soyad</p>
                    <p className="font-medium">{selectedOrder.shipping_address?.name || selectedOrder.customer_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="font-medium">{selectedOrder.shipping_address?.phone || selectedOrder.customer_phone || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Adres</p>
                    <p className="font-medium">
                      {selectedOrder.shipping_address?.address}, {selectedOrder.shipping_address?.district}/{selectedOrder.shipping_address?.city}
                    </p>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-medium mb-3">Ürünler</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Ürün</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Satıcı</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Adet</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Fiyat</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Toplam</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(selectedOrder.items || []).map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {item.image_url && (
                                <img src={item.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                              )}
                              <div>
                                <p className="font-medium text-sm">{item.name}</p>
                                <p className="text-xs text-gray-500">{item.sku}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">{item.store_name || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.quantity}</td>
                          <td className="px-4 py-3 text-right">{(item.price || 0).toLocaleString()} ₺</td>
                          <td className="px-4 py-3 text-right font-medium">{((item.price || 0) * (item.quantity || 1)).toLocaleString()} ₺</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between py-2">
                  <span>Ara Toplam</span>
                  <span>{(selectedOrder.subtotal || 0).toLocaleString()} ₺</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Kargo</span>
                  <span>{(selectedOrder.shipping_cost || 0).toLocaleString()} ₺</span>
                </div>
                {selectedOrder.commission_amount > 0 && (
                  <div className="flex justify-between py-2 text-orange-600">
                    <span>Komisyon</span>
                    <span>{(selectedOrder.commission_amount || 0).toLocaleString()} ₺</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-t font-bold text-lg">
                  <span>Toplam</span>
                  <span>{(selectedOrder.total || 0).toLocaleString()} ₺</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
